import { useRef, useState } from 'react';
import { Paperclip, X } from 'lucide-react';

import { cn } from '@rocco/utils/cn';
import { QUESTIONNAIRE_COLOUR } from '@rocco/components/questionnaire';

import type { MatchAnswers } from '../answers';

/** Matches the Description column's cap, so the server never has to truncate. */
const MAX_DESCRIPTION_LENGTH = 5000;
const MAX_ATTACHMENTS = 5;
const MAX_FILE_BYTES = 4 * 1024 * 1024;

/** Shown once the reader is close enough to the cap for it to matter. */
const COUNTER_VISIBLE_FROM = MAX_DESCRIPTION_LENGTH - 500;

export interface BriefStepProps {
    answers: MatchAnswers;
    update: (patch: Partial<MatchAnswers>) => void;
    placeholder: string;
    uploadLabel?: string;
}

/**
 * The free-text brief, plus anything the reader wants to send with it.
 *
 * Files are rejected loudly rather than dropped: a plan that silently failed to
 * attach is worse than one that was never offered, because the reader believes
 * it went.
 */
export const BriefStep = ({
    answers,
    update,
    placeholder,
    uploadLabel = '+ Upload files',
}: BriefStepProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [rejected, setRejected] = useState<string[]>([]);

    const addFiles = (fileList: FileList | null) => {
        if (!fileList) return;

        const incoming = Array.from(fileList);
        const room = MAX_ATTACHMENTS - answers.attachments.length;

        const tooLarge = incoming.filter((file) => file.size > MAX_FILE_BYTES);
        const withinSize = incoming.filter(
            (file) => file.size <= MAX_FILE_BYTES,
        );
        const overCount = withinSize.slice(room);

        setRejected([
            ...tooLarge.map((file) => `${file.name} is over 4MB`),
            ...overCount.map(
                (file) => `${file.name} — up to ${MAX_ATTACHMENTS} files`,
            ),
        ]);

        if (withinSize.length === 0) return;
        update({
            attachments: [...answers.attachments, ...withinSize.slice(0, room)],
        });
    };

    const removeFile = (name: string) => {
        update({
            attachments: answers.attachments.filter(
                (file) => file.name !== name,
            ),
        });
    };

    const remaining = MAX_DESCRIPTION_LENGTH - answers.description.length;

    return (
        <div className="flex flex-col gap-4">
            <textarea
                aria-label="Your brief"
                className={cn(
                    'min-h-55 w-full resize-y rounded-3 p-5 font-sans text-4 leading-[1.5] outline-none placeholder:text-[#8c8c8c] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#181818]',
                    QUESTIONNAIRE_COLOUR.surface,
                    QUESTIONNAIRE_COLOUR.ink,
                )}
                maxLength={MAX_DESCRIPTION_LENGTH}
                onChange={(event) =>
                    update({ description: event.target.value })
                }
                placeholder={placeholder}
                value={answers.description}
            />

            {answers.description.length >= COUNTER_VISIBLE_FROM ? (
                <p
                    className={cn(
                        'text-right font-sans text-3.25',
                        QUESTIONNAIRE_COLOUR.inkMuted,
                    )}
                >
                    {remaining} characters left
                </p>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
                <button
                    className={cn(
                        'inline-flex h-10.75 items-center rounded-3 px-4.5 font-sans text-4 transition-colors',
                        QUESTIONNAIRE_COLOUR.surface,
                        QUESTIONNAIRE_COLOUR.surfaceHover,
                        QUESTIONNAIRE_COLOUR.ink,
                    )}
                    onClick={() => inputRef.current?.click()}
                    type="button"
                >
                    {uploadLabel}
                </button>

                <input
                    className="hidden"
                    multiple
                    onChange={(event) => {
                        addFiles(event.target.files);
                        // Lets the same file be re-picked after a removal.
                        event.target.value = '';
                    }}
                    ref={inputRef}
                    type="file"
                />
            </div>

            {answers.attachments.length > 0 ? (
                <ul className="flex flex-col gap-1.5">
                    {answers.attachments.map((file) => (
                        <li
                            className={cn(
                                'flex items-center gap-2 rounded-2 px-3.5 py-2.5 font-sans text-3.5',
                                QUESTIONNAIRE_COLOUR.surface,
                                QUESTIONNAIRE_COLOUR.ink,
                            )}
                            key={file.name}
                        >
                            <Paperclip
                                aria-hidden="true"
                                className="size-3.5 shrink-0"
                            />
                            <span className="min-w-0 flex-1 truncate">
                                {file.name}
                            </span>
                            <button
                                aria-label={`Remove ${file.name}`}
                                className="shrink-0 rounded-full p-1 hover:bg-black/5"
                                onClick={() => removeFile(file.name)}
                                type="button"
                            >
                                <X aria-hidden="true" className="size-3.5" />
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}

            {rejected.length > 0 ? (
                <ul className="flex flex-col gap-1" role="alert">
                    {rejected.map((message) => (
                        <li
                            className="font-sans text-3.5 text-red-600"
                            key={message}
                        >
                            {message}
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
};
